import { useState, useEffect, useRef, useMemo } from 'react';
import { apiPost } from './apiClient';
import { useAccessFeatures } from '../../../auth/useAccessFeatures';
import { canUseFeature } from '../../../auth/canUseFeature';
import { useMutedTopics } from '../board/state/useMutedTopics';
import type { EventFromAsset } from './types';
import type {
    UnifiedSearchRequest,
    UnifiedSearchResponse,
    UnifiedAssetResult,
} from './unifiedSearchTypes';

interface HookResult<T> {
    data: T | null;
    loading: boolean;
    error: Error | null;
}

// ---------------------------------------------------------------------------
// Low-level hook: calls POST /api/search with arbitrary params
// ---------------------------------------------------------------------------

export function useUnifiedSearch(
    params: UnifiedSearchRequest | null,
    debounceMs = 300
): HookResult<UnifiedSearchResponse> {
    const [data, setData] = useState<UnifiedSearchResponse | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<Error | null>(null);
    const callIdRef = useRef(0);
    const timerRef = useRef<ReturnType<typeof setTimeout>>();

    const { effective: muted } = useMutedTopics();

    // The key has to move with the preference, or a cached search would outlive a change to it.
    const paramsKey = useMemo(() => {
        if (!params) return null;
        const merged: UnifiedSearchRequest = { ...params };
        if (muted.tags.length > 0) merged.exclude_tags = muted.tags;
        if (muted.categories.length > 0) merged.exclude_categories = muted.categories;
        return JSON.stringify(merged);
    }, [params, muted]);

    useEffect(() => {
        clearTimeout(timerRef.current);

        if (paramsKey === null) {
            // Bump the call id so an in-flight response settling in this tick
            // cannot write back over the cleared state.
            ++callIdRef.current;
            setData(null);
            setError(null);
            setLoading(false);
            return;
        }

        setLoading(true);
        const callId = ++callIdRef.current;
        const parsed: UnifiedSearchRequest = JSON.parse(paramsKey);

        const delay = (parsed.query?.length ?? 0) === 0 ? 0 : debounceMs;

        const controller = new AbortController();
        timerRef.current = setTimeout(() => {
            apiPost<UnifiedSearchResponse>(
                '/api/search',
                parsed,
                controller.signal
            )
                .then(res => {
                    if (callId !== callIdRef.current) return;
                    setData(res);
                    setError(null);
                })
                .catch(err => {
                    if (
                        err?.name === 'AbortError' ||
                        callId !== callIdRef.current
                    )
                        return;
                    console.error('unified-search failed:', err);
                    setError(err);
                    setData(null);
                })
                .finally(() => {
                    if (callId !== callIdRef.current) return;
                    setLoading(false);
                });
        }, delay);

        return () => {
            clearTimeout(timerRef.current);
            controller.abort();
        };
    }, [paramsKey, debounceMs]);

    return { data, loading, error };
}

// ---------------------------------------------------------------------------
// EventsPanel hook: events only, with optional asset context
// ---------------------------------------------------------------------------

interface EventSearchResult {
    activeEvents: EventFromAsset[];
    closedEvents: EventFromAsset[];
    isDefault: boolean;
    loading: boolean;
    error: Error | null;
}

const EMPTY_EVENTS: EventFromAsset[] = [];

export function useEventSearch(
    query: string,
    assetContext?: { ticker: string; assetType: string } | null,
    limit = 50
): EventSearchResult {
    const params = useMemo<UnifiedSearchRequest>(
        () => ({
            query: query || undefined,
            limit,
            include_assets: false,
            include_active_events: true,
            include_closed_events: !!query,
            asset_context: assetContext?.ticker ?? null,
            asset_context_type: assetContext?.assetType ?? null,
        }),
        [query, limit, assetContext?.ticker, assetContext?.assetType]
    );

    const { data, loading, error } = useUnifiedSearch(params);

    return {
        activeEvents: data?.active_events ?? EMPTY_EVENTS,
        closedEvents: data?.closed_events ?? EMPTY_EVENTS,
        isDefault: !query,
        loading,
        error,
    };
}

// ---------------------------------------------------------------------------
// Header search hook: assets + active events, no closed
// ---------------------------------------------------------------------------

interface HeaderSearchResult {
    assets: UnifiedAssetResult[];
    activeEvents: EventFromAsset[];
    loading: boolean;
    error: Error | null;
}

export function useHeaderSearch(
    query: string,
    limit = 10,
    opts: { scoped?: boolean } = {}
): HeaderSearchResult {
    // scoped: false is for pickers that only feed a chart overlay and never
    // navigate — the asset overlay on the event chart is part of event
    // analysis, so an events-only user may use it.
    const scoped = opts.scoped !== false;
    // Search spans both halves of the split, so scope it to what the user may
    // actually open — otherwise results advertise views that then deny them.
    const access = useAccessFeatures();
    const hasAny = access.features.length > 0 || access.role === 'admin';
    const canAssets = scoped ? canUseFeature(access, 'assets') : hasAny;
    const canEvents = scoped ? canUseFeature(access, 'events') : hasAny;

    // Null params mean "don't call". canUseFeature is false while identity is
    // pending, so this also skips the throwaway request that features would
    // immediately supersede. /api/search is a Cloud Function invocation in
    // production, so a user who can see neither half never calls it.
    const params = useMemo<UnifiedSearchRequest | null>(
        () =>
            !canAssets && !canEvents
                ? null
                : {
                      query: query || undefined,
                      limit,
                      include_assets: canAssets,
                      include_active_events: canEvents,
                      include_closed_events: false,
                  },
        [query, limit, canAssets, canEvents]
    );

    const { data, loading, error } = useUnifiedSearch(params);

    // Belt and braces: never surface a half the user lacks, even if the
    // backend ignores the include_* flags.
    return {
        assets: canAssets ? data?.assets ?? [] : [],
        activeEvents: canEvents ? data?.active_events ?? [] : [],
        loading,
        error,
    };
}
